"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { TrendingUp, Bot, Zap, ShieldCheck, Activity, CircleCheck as CheckCircle2, ArrowUpRight, Sparkles, RefreshCw, Search, ChartBar as BarChart3, Check } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "@/components/common/Link";
const kpiItems = [{
  id: "kpi-1",
  label: "AI CITATION VOLUME",
  value: "+342%",
  progress: 88,
  description: "Across ChatGPT, Perplexity & Claude",
  icon: TrendingUp
}, {
  id: "kpi-2",
  label: "PROMPT SHARE",
  value: "88.4%",
  progress: 88,
  description: "Top-3 local recommendation rate",
  icon: Bot
}, {
  id: "kpi-3",
  label: "QUALIFIED LEADS",
  value: "+215%",
  progress: 75,
  description: "Direct bookings from AI referrals",
  icon: Zap
}, {
  id: "kpi-4",
  label: "GEO AUDIT SCORE",
  value: "98/100",
  progress: 98,
  description: "Verified structured schema & citations",
  icon: ShieldCheck
}];
const metricComparisons = [{
  id: "m-1",
  label: "ChatGPT Recommendation Rate",
  beforeValue: 14,
  afterValue: 89,
  beforeLabel: "14%",
  afterLabel: "89%",
  diff: "+75%",
  detail: "Measured across 120+ high-intent local prompts"
}, {
  id: "m-2",
  label: "Perplexity Citation Inclusion",
  beforeValue: 8,
  afterValue: 94,
  beforeLabel: "8%",
  afterLabel: "94%",
  diff: "+86%",
  detail: "Verified source links in generative AI answers"
}, {
  id: "m-3",
  label: "Claude Local Brand Share",
  beforeValue: 12,
  afterValue: 82,
  beforeLabel: "12%",
  afterLabel: "82%",
  diff: "+70%",
  detail: "First-tier entity recognition and inclusion"
}, {
  id: "m-4",
  label: "Gemini Local Pack Alignment",
  beforeValue: 18,
  afterValue: 91,
  beforeLabel: "18%",
  afterLabel: "91%",
  detail: "Maps & structured data sync verification"
}];
const activityLogs = [{
  id: "act-1",
  time: "Just now",
  engine: "ChatGPT 4o",
  business: "Aura Dental Care",
  query: "Who is the top cosmetic dentist near me?",
  status: "Rank #1 Recommended",
  confidence: "99.2%"
}, {
  id: "act-2",
  time: "3 mins ago",
  engine: "Perplexity AI",
  business: "Apex HVAC Services",
  query: "Best emergency AC repair technician in Phoenix",
  status: "Cited Source Link",
  confidence: "98.7%"
}, {
  id: "act-3",
  time: "7 mins ago",
  engine: "Claude 3.5",
  business: "Vanguard Legal Group",
  query: "Reliable commercial litigation attorney",
  status: "Primary Entity Result",
  confidence: "97.9%"
}, {
  id: "act-4",
  time: "12 mins ago",
  engine: "Gemini AI",
  business: "Summit Plumbing Co.",
  query: "Licensed master plumber for commercial job",
  status: "Schema Match Confirmed",
  confidence: "99.5%"
}];
export default function Dashboard() {
  const prefersReducedMotion = useReducedMotion();
  const [activeLogIndex, setActiveLogIndex] = useState(0);
  const [isLiveStreaming, setIsLiveStreaming] = useState(true);
  useEffect(() => {
    if (!isLiveStreaming || prefersReducedMotion) return;
    const interval = setInterval(() => {
      setActiveLogIndex(prev => (prev + 1) % activityLogs.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isLiveStreaming, prefersReducedMotion]);
  const handleNextLog = () => {
    setActiveLogIndex(prev => (prev + 1) % activityLogs.length);
  };
  return <section id="measurableresultsdashboard" data-nav="dark" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden border-b border-border/40">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(352_54%_33%_/_0.08),transparent_50%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(0_0%_100%_/_0.03)_1px,transparent_1px),linear-gradient(to_bottom,hsl(0_0%_100%_/_0.03)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div className="max-w-3xl mb-16">
          <motion.div initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="flex items-center gap-2 mb-4">
            <span className="h-2 w-2 rounded-full bg-[var(--accent-deep-red)] animate-pulse" />
            <span className="font-mono text-xs tracking-widest text-[var(--accent-deep-red)] uppercase">
              {"/// SYSTEM ANALYTICS & VERIFIED PROOF ///"}
            </span>
          </motion.div>

          <motion.h2 initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="font-serif text-3xl sm:text-4xl md:text-5xl font-normal tracking-tight text-foreground mb-6">
            Quantifiable AI <span className="italic text-[var(--accent-deep-red)] font-serif">visibility</span> that drives direct revenue
          </motion.h2>

          <motion.p initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-muted-foreground text-base md:text-lg font-sans leading-relaxed">
            ClearLine optimizes local service businesses to ensure when prospective customers ask AI search engines who to hire, your business is recommended first.
          </motion.p>
        </div>

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {kpiItems.map((kpi, i) => {
          const Icon = kpi.icon;
          return <motion.div key={kpi.id} data-index={i} initial={prefersReducedMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }}>
                <Card className="bg-card/60 backdrop-blur-sm border-border/60 hover:border-[var(--accent-deep-red)]/40 transition-colors h-full flex flex-col justify-between">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-muted-foreground tracking-wider">
                        {kpi.label}
                      </span>
                      <div className="p-2 rounded-md bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)]">
                        <Icon className="size-4" />
                      </div>
                    </div>
                    <CardTitle className="font-serif text-3xl sm:text-4xl font-normal text-foreground mt-2">
                      {kpi.value}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    <Progress value={kpi.progress} className="h-1.5 bg-muted/40 [&>div]:bg-[var(--accent-deep-red)]" />
                    <p className="text-xs text-muted-foreground font-sans">
                      {kpi.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          {}
          <Card className="lg:col-span-7 bg-card/60 backdrop-blur-sm border-border/60 p-6 md:p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-border/40">
                <div>
                  <h3 className="font-serif text-xl md:text-2xl font-normal text-foreground">
                    Performance Benchmark
                  </h3>
                  <p className="text-xs font-mono text-muted-foreground mt-1">
                    Traditional SEO vs. ClearLine AI Optimization
                  </p>
                </div>
                <Badge variant="outline" className="font-mono text-xs border-[var(--accent-deep-red)]/40 text-[var(--accent-deep-red)] bg-[var(--accent-deep-red)]/5">
                  <BarChart3 className="size-3 mr-1" /> Verified Data
                </Badge>
              </div>

              <TooltipProvider>
                <div className="space-y-6">
                  {metricComparisons.map((item, i) => <div key={item.id} data-index={i} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="font-sans font-medium text-foreground cursor-help underline decoration-muted-foreground/40 underline-offset-4">
                              {item.label}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="bg-popover text-popover-foreground border-border max-w-xs">
                            <p className="text-xs">{item.detail}</p>
                          </TooltipContent>
                        </Tooltip>
                        <span className="font-mono text-xs font-semibold text-[var(--accent-deep-red)]">
                          {item.diff} Growth
                        </span>
                      </div>

                      {}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
                          <span>Before ClearLine ({item.beforeLabel})</span>
                          <span>With ClearLine ({item.afterLabel})</span>
                        </div>
                        <div className="h-3 w-full bg-muted/30 rounded-full overflow-hidden flex relative">
                          <motion.div initial={prefersReducedMotion ? {
                        width: `${item.beforeValue}%`
                      } : {
                        width: "0%"
                      }} whileInView={{
                        width: `${item.beforeValue}%`
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 0.8,
                        ease: "easeOut"
                      }} className="bg-muted-foreground/30 h-full border-r border-background/20" />
                          <motion.div initial={prefersReducedMotion ? {
                        width: `${item.afterValue - item.beforeValue}%`
                      } : {
                        width: "0%"
                      }} whileInView={{
                        width: `${item.afterValue - item.beforeValue}%`
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 1,
                        delay: 0.2,
                        ease: "easeOut"
                      }} className="bg-[var(--accent-deep-red)] h-full" />
                        </div>
                      </div>
                    </div>)}
                </div>
              </TooltipProvider>
            </div>

            <div className="mt-8 pt-6 border-t border-border/40 flex flex-wrap items-center justify-between gap-4 text-xs font-mono text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-muted-foreground/40 inline-block" />
                <span>Baseline Search Visibility</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-[var(--accent-deep-red)] inline-block" />
                <span>ClearLine Generative Engine Dominance</span>
              </div>
            </div>
          </Card>

          {}
          <Card className="lg:col-span-5 bg-card/60 backdrop-blur-sm border-border/60 p-6 md:p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-border/40">
                <div className="flex items-center gap-2">
                  <Activity className="size-4 text-[var(--accent-deep-red)] animate-pulse" />
                  <h3 className="font-serif text-xl font-normal text-foreground">
                    Live Citation Stream
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => setIsLiveStreaming(!isLiveStreaming)} title={isLiveStreaming ? "Pause Stream" : "Resume Stream"} className="size-8 text-muted-foreground hover:text-foreground">
                    <RefreshCw className={`size-3.5 ${isLiveStreaming ? "animate-spin" : ""}`} />
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleNextLog} className="text-xs font-mono h-8 border-border">
                    Next Event
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                <AnimatePresence mode="wait">
                  {activityLogs.map((log, index) => {
                  if (index !== activeLogIndex) return null;
                  return <motion.div key={log.id} data-index={index} initial={prefersReducedMotion ? {
                    opacity: 1
                  } : {
                    opacity: 0,
                    x: 20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={prefersReducedMotion ? {
                    opacity: 0
                  } : {
                    opacity: 0,
                    x: -20
                  }} transition={{
                    duration: 0.3
                  }} className="p-4 rounded-lg bg-background/80 border border-border/80 space-y-3">
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary" className="font-mono text-xs bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] border-none">
                            {log.engine}
                          </Badge>
                          <span className="font-mono text-xs text-muted-foreground">{log.time}</span>
                        </div>

                        <div>
                          <p className="text-xs font-mono text-muted-foreground mb-1">
                            Target Entity: <span className="text-foreground font-sans font-medium">{log.business}</span>
                          </p>
                          <p className="text-sm font-sans text-foreground italic bg-muted/20 p-2.5 rounded border border-border/30">
                            &quot;{log.query}&quot;
                          </p>
                        </div>

                        <Separator className="bg-border/40" />

                        <div className="flex items-center justify-between text-xs font-mono">
                          <span className="text-[var(--accent-deep-red)] font-semibold flex items-center gap-1">
                            <CheckCircle2 className="size-3.5" /> {log.status}
                          </span>
                          <span className="text-muted-foreground">Confidence: {log.confidence}</span>
                        </div>
                      </motion.div>;
                })}
                </AnimatePresence>

                {}
                <div className="space-y-2 pt-2">
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2">
                    Recent Verification Queue
                  </p>
                  {activityLogs.map((item, i) => <div key={item.id} data-index={i} onClick={() => setActiveLogIndex(i)} className={`p-2.5 rounded text-xs font-sans flex items-center justify-between cursor-pointer transition-colors ${i === activeLogIndex ? "bg-[var(--accent-deep-red)]/10 text-foreground border border-[var(--accent-deep-red)]/30" : "bg-background/40 text-muted-foreground hover:bg-background/70 border border-transparent"}`}>
                      <div className="flex items-center gap-2 truncate pr-2">
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {item.engine}
                        </span>
                        <span className="truncate">{item.business}</span>
                      </div>
                      <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                        {item.time}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {}
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="rounded-xl bg-card border border-[var(--accent-deep-red)]/30 p-8 md:p-12 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl text-center md:text-left">
            <h3 className="font-serif text-2xl md:text-3xl font-normal text-foreground">
              Ready to claim your place in AI recommendations?
            </h3>
            <p className="text-muted-foreground text-sm md:text-base font-sans">
              Get an immediate AI visibility audit and see how your competitors rank when customers search.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full md:w-auto">
            <Button size="lg" className="w-full sm:w-auto bg-[var(--accent-deep-red)] text-white hover:bg-[var(--accent-deep-red)]/90" asChild>
              <Link to="/ContactPage"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-up-right size-4 ml-1" aria-hidden="true"><path d="M7 7h10v10">Get started </path><path d="M7 17 17 7"></path></svg></Link>
            </Button>
            <Button size="lg" variant="outline" className="w-full sm:w-auto border-border text-foreground hover:bg-accent" asChild>
              <Link to="/VisibilityCheckupPage">
                Free AI Checkup
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}